import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Drawer, Empty, Form, Input, Modal, Popconfirm, Row, Segmented, Select, Space, Statistic, Table, Tag, Tooltip, Tree, Typography, message } from 'antd';
import { ClockCircleOutlined, DownloadOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import * as echarts from 'echarts';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// ══════════════════════════════════════════════
// TODO: 联调时替换为接口数据
// 口径：设备 = License；品牌归属由 License 与 App 的绑定关系决定（按当前绑定唯一归属）
// 维度：品牌 = App 名称；版本 = App名称-系统-区域（图表与导出的最小统计粒度）
// 数据来源：数仓按日推送（T+1），非实时
// ══════════════════════════════════════════════

// mock 角色视角：真实业务由登录角色决定（平台运营 / App 应用服务商 / 设备制造商）
const ROLE_OPTIONS = [
    { label: '平台视角', value: 'platform' },
    { label: 'App企业视角', value: 'app' },
    { label: '设备企业视角', value: 'device' },
];

// 趋势图粒度：按周看近 8 周 / 按月看近 12 个月
const TREND_UNIT_OPTIONS = [
    { label: '按周', value: 'week' },
    { label: '按月', value: 'month' },
];
const TREND_WINDOW = { week: 8, month: 12 };

// 原型数据起点（累计绑定的起始点，联调替换为最早绑定时间）
const DATA_START = '2024-01-01';

// 数仓按日推送（T+1）
// TODO: 联调时替换为接口返回的最后更新时点
const LAST_UPDATE_TIME = `${dayjs().format('YYYY-MM-DD')} 02:00`;
const DATA_AS_OF_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

// 导出时间范围（按天导出，与页面其余部分无关）
const EXPORT_RANGE_OPTIONS = [
    { label: '近7日', value: '7d' },
    { label: '近30天', value: '30d' },
    { label: '本月', value: 'thisMonth' },
    { label: '上月', value: 'lastMonth' },
    { label: '近6个月', value: '6m' },
    { label: '近1年', value: '1y' },
];

const buildExportRange = (key) => {
    switch (key) {
        case '7d':
            return [dayjs().subtract(6, 'day'), dayjs()];
        case '30d':
            return [dayjs().subtract(29, 'day'), dayjs()];
        case 'thisMonth':
            return [dayjs().startOf('month'), dayjs()];
        case 'lastMonth':
            return [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')];
        case '6m':
            return [dayjs().subtract(6, 'month'), dayjs()];
        default:
            return [dayjs().subtract(1, 'year'), dayjs()];
    }
};

// 平台视角下的「所属企业」筛选（联调时替换为企业远端搜索）
const MOCK_ENTERPRISES = [
    { value: 'all', label: '全部企业', factor: 1 },
    { value: 'JGSf0Nd7', label: '东莞康林物联网有限公司', factor: 0.22 },
    { value: 'GZ-YL-002', label: '广州云联物联网有限公司', factor: 0.14 },
    { value: 'SZ-MY-003', label: '深圳铭远智能科技有限公司', factor: 0.09 },
];

// 各视角的品牌设备数（total = 累计绑定设备数）
const MOCK_BRANDS = {
    // 平台运营：全平台有绑定设备的 App
    platform: [
        { brand: '牵心Pro', color: '#5470c6', total: 2450, seed: 3, versions: [
            { name: '牵心Pro-iOS-全球', count: 1250 },
            { name: '牵心Pro-Android-全球', count: 1200 },
        ] },
        { brand: '牵心', color: '#91cc75', total: 1180, seed: 7, versions: [
            { name: '牵心-iOS-国内', count: 620 },
            { name: '牵心-Android-国内', count: 560 },
        ] },
        { brand: 'Alaga', color: '#fac858', total: 540, seed: 11, versions: [
            { name: 'Alaga-iOS-海外', count: 300 },
            { name: 'Alaga-Android-海外', count: 240 },
        ] },
        { brand: 'Glascam', color: '#ee6666', total: 310, seed: 17, versions: [
            { name: 'Glascam-iOS-海外', count: 170 },
            { name: 'Glascam-Android-海外', count: 140 },
        ] },
    ],
    // App 应用服务商：仅本企业名下 App
    app: [
        { brand: '牵心Pro', color: '#5470c6', total: 2450, seed: 3, versions: [
            { name: '牵心Pro-iOS-全球', count: 1250 },
            { name: '牵心Pro-Android-全球', count: 1200 },
        ] },
        { brand: '牵心', color: '#91cc75', total: 1180, seed: 7, versions: [
            { name: '牵心-iOS-国内', count: 620 },
            { name: '牵心-Android-国内', count: 560 },
        ] },
    ],
    // 设备制造商：仅本企业 License，展示其绑定到的 App 品牌分布
    device: [
        { brand: '牵心Pro', color: '#5470c6', total: 820, seed: 3, versions: [
            { name: '牵心Pro-iOS-全球', count: 430 },
            { name: '牵心Pro-Android-全球', count: 390 },
        ] },
        { brand: 'Alaga', color: '#fac858', total: 540, seed: 11, versions: [
            { name: 'Alaga-iOS-海外', count: 300 },
            { name: 'Alaga-Android-海外', count: 240 },
        ] },
        { brand: 'Glascam', color: '#ee6666', total: 310, seed: 17, versions: [
            { name: 'Glascam-iOS-海外', count: 170 },
            { name: 'Glascam-Android-海外', count: 140 },
        ] },
    ],
};

// ══════════════════════════════════════════════
// 屏蔽配置（平台管理端）
// 场景：平台测试人员在企业未知的 App 上测试产生脏数据，企业质疑 → 指定企业，屏蔽指定 App 的设备绑定数据
// 屏蔽粒度：App-系统-区域（一个 AppID 对应一个「App 名称-系统-区域」）
// 长期记忆：原型存 localStorage；TODO: 联调时替换为服务端接口（按配置 ID 增删改查）
// ══════════════════════════════════════════════
const BLOCK_STORAGE_KEY = 'device_brand_block_configs';

// 屏蔽配置查询条件（空态）
const EMPTY_BLOCK_QUERY = {
    appName: '',
    appId: '',
    appEntName: '',
    appEntId: '',
    entName: '',
    entId: '',
};

// App-系统-区域 全集（屏蔽可选范围，含 AppID 与归属企业）
const MOCK_APP_VERSIONS = [
    { appId: '100001', version: '牵心Pro-iOS-全球', entId: 'JGSf0Nd7', entName: '东莞康林物联网有限公司' },
    { appId: '100002', version: '牵心Pro-Android-全球', entId: 'JGSf0Nd7', entName: '东莞康林物联网有限公司' },
    { appId: '100003', version: '牵心-iOS-国内', entId: 'JGSf0Nd7', entName: '东莞康林物联网有限公司' },
    { appId: '100004', version: '牵心-Android-国内', entId: 'JGSf0Nd7', entName: '东莞康林物联网有限公司' },
    { appId: '100005', version: 'Alaga-iOS-海外', entId: 'GZ-YL-002', entName: '广州云联物联网有限公司' },
    { appId: '100006', version: 'Alaga-Android-海外', entId: 'GZ-YL-002', entName: '广州云联物联网有限公司' },
    { appId: '100007', version: 'Glascam-iOS-海外', entId: 'SZ-MY-003', entName: '深圳铭远智能科技有限公司' },
    { appId: '100008', version: 'Glascam-Android-海外', entId: 'SZ-MY-003', entName: '深圳铭远智能科技有限公司' },
];

const APP_VERSION_MAP = MOCK_APP_VERSIONS.reduce((m, a) => { m[a.appId] = a; return m; }, {});

// 一组 App（AppID）的归属企业去重列表
const ownersOfApps = (appIds) => {
    const owners = [];
    appIds.forEach((id) => {
        const app = APP_VERSION_MAP[id];
        if (app && !owners.some((o) => o.entId === app.entId)) {
            owners.push({ entId: app.entId, entName: app.entName });
        }
    });
    return owners;
};

// 屏蔽 App 明细树：一级 = App企业（名称 + 企业ID）；二级 = App名称-系统-区域 + AppID
const buildBlockAppTree = (appIds) => {
    const known = appIds.filter((id) => APP_VERSION_MAP[id]);
    const unknown = appIds.filter((id) => !APP_VERSION_MAP[id]);
    const nodes = ownersOfApps(appIds).map((owner) => ({
        key: `ent-${owner.entId}`,
        title: `${owner.entName} ${owner.entId}`,
        children: known
            .filter((id) => APP_VERSION_MAP[id].entId === owner.entId)
            .map((id) => ({
                key: id,
                title: `${APP_VERSION_MAP[id].version} ${id}`,
            })),
    }));
    if (unknown.length > 0) {
        nodes.push({
            key: 'ent-unknown',
            title: '未知企业（App 信息已失效）',
            children: unknown.map((id) => ({ key: id, title: `${id}（App 信息已失效）` })),
        });
    }
    return nodes;
};

// 屏蔽配置（一条 = 指定企业 + 被屏蔽的 App-系统-区域 列表）
// TODO: 联调时替换为接口数据
const MOCK_BLOCK_CONFIGS = [
    {
        id: 'BLK-20260915-001',
        entId: 'JGSf0Nd7',
        entName: '东莞康林物联网有限公司',
        appIds: ['100001', '100003', '100004'],
        operator: '平台运营-张三',
        updatedAt: '2026-09-15 10:24',
    },
    {
        id: 'BLK-20260918-002',
        entId: 'GZ-YL-002',
        entName: '广州云联物联网有限公司',
        appIds: ['100006'],
        operator: '平台运营-李四',
        updatedAt: '2026-09-18 16:05',
    },
    {
        id: 'BLK-20260920-003',
        entId: 'SZ-MY-003',
        entName: '深圳铭远智能科技有限公司',
        appIds: ['100001', '100005'],
        operator: '平台运营-张三',
        updatedAt: '2026-09-20 09:41',
    },
];

// 企业端 mock：App企业 / 设备企业登录后的「本企业」（东莞康林物联网有限公司）
// 企业端登录时，取该企业在平台侧配置的屏蔽项，直接过滤（不展示，且无任何屏蔽提示）
// TODO: 联调时替换为登录态返回的企业 ID
const MY_ENTERPRISE_ID = 'JGSf0Nd7';

const readBlockConfigs = () => {
    try {
        const raw = localStorage.getItem(BLOCK_STORAGE_KEY);
        const list = raw ? JSON.parse(raw) : null;
        return Array.isArray(list) ? list : MOCK_BLOCK_CONFIGS;
    } catch (e) {
        return MOCK_BLOCK_CONFIGS;
    }
};

const writeBlockConfigs = (list) => {
    try {
        localStorage.setItem(BLOCK_STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
        // 忽略存储异常（隐私模式等），仅本次会话生效
    }
};

// 字符串散列 → 稳定种子（保证同一日期在各粒度下取值一致）
const hashSeed = (key, seed) => {
    let h = seed * 131 + 17;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 1000003;
    return h;
};

// 数据底座：某品牌某一天的「新增绑定设备数」（确定性生成，仅内部用于派生累计存量）
const dailyBound = (brand, dateKey) => {
    const h = hashSeed(dateKey, brand.seed);
    const base = brand.total / 380;
    const r1 = (h % 1000) / 1000;
    const r2 = (Math.floor(h / 7) % 1000) / 1000;
    return Math.max(1, Math.round(base * (0.72 + r1 * 0.7) * (0.9 + r2 * 0.25)));
};

// 各品牌「每日新增绑定」序列（数据起点 → 数据截止日）+ 前缀和
const buildDailySeries = (brands) => {
    const startDate = dayjs(DATA_START).startOf('day');
    const endDate = dayjs(DATA_AS_OF_DATE).startOf('day');
    const totalDays = Math.max(1, endDate.diff(startDate, 'day') + 1);
    const keys = [];
    const index = new Map();
    for (let i = 0; i < totalDays; i++) {
        const k = startDate.add(i, 'day').format('YYYY-MM-DD');
        keys.push(k);
        index.set(k, i);
    }
    return brands.map((b) => {
        const values = new Array(totalDays);
        const prefix = new Array(totalDays);
        let acc = 0;
        for (let i = 0; i < totalDays; i++) {
            const v = dailyBound(b, keys[i]);
            values[i] = v;
            acc += v;
            prefix[i] = acc;
        }
        return { brand: b.brand, keys, index, values, prefix };
    });
};

// 截至某日的原始累计值（越界自动收敛到可用数据范围）
const prefixDaily = (series, key) => {
    const i = series.index.get(key);
    return series.prefix[i === undefined ? series.values.length - 1 : i];
};

// 某品牌在 [startKey, endKey] 区间内的新增绑定设备数（越界自动收敛到可用数据范围）
const sumDailyBetween = (series, startKey, endKey) => {
    if (!series) return 0;
    const firstKey = series.keys[0];
    const lastKey = series.keys[series.keys.length - 1];
    if (endKey < firstKey || startKey > lastKey) return 0;
    const sKey = startKey < firstKey ? firstKey : startKey;
    const eKey = endKey > lastKey ? lastKey : endKey;
    if (sKey > eKey) return 0;
    const si = series.index.get(sKey);
    const ei = series.index.get(eKey);
    return series.prefix[ei] - (si > 0 ? series.prefix[si - 1] : 0);
};

// 某品牌截至某日的累计绑定设备数（按数据截止日归一）
const brandTotalAt = (brand, dateKey, series) => {
    if (!series) return 0;
    const rawAll = series.prefix[series.prefix.length - 1];
    const rawAt = prefixDaily(series, dateKey);
    return rawAll > 0 ? Math.round((brand.total * rawAt) / rawAll) : 0;
};

// 每周起点（周一）
const mondayOf = (d) => {
    const wd = d.day() === 0 ? 7 : d.day();
    return d.startOf('day').subtract(wd - 1, 'day');
};

// 新增绑定的 5 个周期窗口：昨日 / 本周（周一起）/ 近7日 / 本月 / 近30日
// 数据数仓 T+1，所有窗口均以数据截止日为终点；窗口之间允许重叠
const buildGrowthPeriods = () => {
    const asOf = dayjs(DATA_AS_OF_DATE).startOf('day');
    return [
        { key: 'yesterday', label: '昨日', start: asOf, end: asOf },
        { key: 'thisWeek', label: '本周', start: mondayOf(asOf), end: asOf },
        { key: 'last7', label: '近7日', start: asOf.subtract(6, 'day'), end: asOf },
        { key: 'thisMonth', label: '本月', start: asOf.startOf('month'), end: asOf },
        { key: 'last30', label: '近30日', start: asOf.subtract(29, 'day'), end: asOf },
    ];
};

// 趋势窗口的 X 轴刻度：按周近 8 周（标签取周一日期）/ 按月近 12 个月
const buildTrendPeriods = (unit) => {
    const today = dayjs(DATA_AS_OF_DATE).startOf('day');
    const points = [];
    if (unit === 'week') {
        const lastMonday = mondayOf(today);
        for (let i = TREND_WINDOW.week - 1; i >= 0; i--) {
            const mon = lastMonday.subtract(i * 7, 'day');
            points.push({
                key: mon.format('YYYY-MM-DD'),
                endKey: mon.add(6, 'day').format('YYYY-MM-DD'),
                label: `${mon.month() + 1}月${mon.date()}日`,
            });
        }
    } else {
        for (let i = TREND_WINDOW.month - 1; i >= 0; i--) {
            const m = today.subtract(i, 'month');
            points.push({
                key: m.format('YYYY-MM'),
                endKey: m.endOf('month').format('YYYY-MM-DD'),
                label: m.format('YYYY-MM'),
            });
        }
    }
    return points;
};

// 按企业系数折算品牌数据（末个版本补齐误差，保证版本之和等于品牌总数）
const scaleBrands = (brands, factor) => {
    if (factor === 1) return brands;
    return brands.map((b) => {
        const targetCount = Math.round(b.total * factor);
        let countAcc = 0;
        const versions = b.versions.map((v, idx) => {
            const last = idx === b.versions.length - 1;
            const count = last ? targetCount - countAcc : Math.round(v.count * factor);
            countAcc += count;
            return { ...v, count };
        });
        return { ...b, versions, total: countAcc };
    });
};

// 按天构建导出行：每一天 × 每个 App版本（App名称-系统-区域）一行
// 累计绑定设备数 = 截至该日的累计值；当日新增绑定数 = 该日新发生的绑定数
// 同一天末个版本补齐误差，保证各版本之和 = 该日品牌合计
const buildExportRows = (exportBrands, exportRange, seriesMap) => {
    const start = exportRange[0].startOf('day');
    const end = exportRange[1].endOf('day');
    const days = end.diff(start, 'day') + 1;
    const rows = [];
    for (let i = 0; i < days; i++) {
        const dateText = start.add(i, 'day').format('YYYY-MM-DD');
        exportBrands.forEach((b) => {
            const brandBound = brandTotalAt(b, dateText, seriesMap[b.brand]);
            const brandAdded = dailyBound(b, dateText);
            const lastIdx = b.versions.length - 1;
            let boundAcc = 0;
            let addedAcc = 0;
            b.versions.forEach((v, idx) => {
                const bound = idx === lastIdx
                    ? brandBound - boundAcc
                    : Math.round((brandBound * v.count) / (b.total || 1));
                const added = idx === lastIdx
                    ? brandAdded - addedAcc
                    : Math.round((brandAdded * v.count) / (b.total || 1));
                boundAcc += bound;
                addedAcc += added;
                rows.push({ date: dateText, version: v.name, bound, added });
            });
        });
    }
    return rows;
};

const DeviceBrand = () => {
    const [mockRole, setMockRole] = useState('platform');
    const [entId, setEntId] = useState('all');
    const [trendUnit, setTrendUnit] = useState('week');

    // ─── 导出 Modal 状态 ───
    const [exportVisible, setExportVisible] = useState(false);
    const [exportRangeKey, setExportRangeKey] = useState(null);
    const [exportPreview, setExportPreview] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    // ─── 屏蔽配置（平台管理端）状态 ───
    const [blockVisible, setBlockVisible] = useState(false);
    const [blockConfigs, setBlockConfigs] = useState(() => readBlockConfigs());
    // blockQuery = 已生效的查询条件；blockQueryInput = 输入中的查询条件（点「查询」后生效）
    const [blockQuery, setBlockQuery] = useState({ ...EMPTY_BLOCK_QUERY });
    const [blockQueryInput, setBlockQueryInput] = useState({ ...EMPTY_BLOCK_QUERY });
    const [blockFormVisible, setBlockFormVisible] = useState(false);
    const [editingBlock, setEditingBlock] = useState(null);
    // 屏蔽 App 明细弹窗（点击列表「屏蔽 App」数量打开）
    const [blockDetail, setBlockDetail] = useState(null);
    const [blockForm] = Form.useForm();

    const isPlatform = mockRole === 'platform';

    // 平台视角可按企业筛选；企业视角固定为本企业
    const entFactor = isPlatform
        ? (MOCK_ENTERPRISES.find((e) => e.value === entId)?.factor ?? 1)
        : 1;

    // 平台端：平台视角选定某个企业时，该企业命中的屏蔽配置（App-系统-区域 级别）
    const blockedVersions = useMemo(() => {
        if (!isPlatform || entId === 'all') return [];
        const ids = new Set(
            blockConfigs.filter((c) => c.entId === entId).flatMap((c) => c.appIds),
        );
        return MOCK_APP_VERSIONS.filter((a) => ids.has(a.appId)).map((a) => a.version);
    }, [isPlatform, entId, blockConfigs]);

    // 企业端：App企业 / 设备企业登录后，取「本企业」在平台侧已配置的屏蔽项
    // 企业端为「不展示」——被屏蔽的 App 绑定设备直接从页面过滤掉，且不出现任何屏蔽提示/角标
    const enterpriseBlockedVersions = useMemo(() => {
        if (isPlatform) return [];
        const ids = new Set(
            blockConfigs.filter((c) => c.entId === MY_ENTERPRISE_ID).flatMap((c) => c.appIds),
        );
        return MOCK_APP_VERSIONS.filter((a) => ids.has(a.appId)).map((a) => a.version);
    }, [isPlatform, blockConfigs]);

    const blockedSet = useMemo(
        () => new Set(isPlatform ? blockedVersions : enterpriseBlockedVersions),
        [isPlatform, blockedVersions, enterpriseBlockedVersions],
    );

    // 卡片区展示数据
    // - 平台端：保留被屏蔽的 App 卡片，版本打「已屏蔽」标记（不删除，保留原数据量供核对）
    // - 企业端：被屏蔽的 App-系统-区域 直接不展示（版本剔除，卡片可能整体消失）
    const brands = useMemo(() => {
        const base = scaleBrands(MOCK_BRANDS[mockRole] || [], entFactor);
        if (!isPlatform) {
            return base
                .map((b) => ({
                    ...b,
                    versions: b.versions
                        .filter((v) => !blockedSet.has(v.name))
                        .map((v) => ({ ...v, blocked: false })),
                }))
                .filter((b) => b.versions.length > 0)
                .map((b) => ({ ...b, blockedVersionCount: 0, blockedTotal: 0, allBlocked: false }));
        }
        return base.map((b) => {
            const versions = b.versions.map((v) => ({ ...v, blocked: blockedSet.has(v.name) }));
            const blockedList = versions.filter((v) => v.blocked);
            return {
                ...b,
                versions,
                blockedVersionCount: blockedList.length,
                blockedTotal: blockedList.reduce((sum, v) => sum + v.count, 0),
                allBlocked: versions.length > 0 && blockedList.length === versions.length,
            };
        });
    }, [mockRole, entFactor, isPlatform, blockedSet]);

    // 参与统计的数据：剔除被屏蔽的 App-系统-区域（图表 / 导出 / 合计均使用它）
    const statBrands = useMemo(() => brands
        .map((b) => {
            const versions = b.versions.filter((v) => !v.blocked);
            return {
                ...b,
                versions,
                total: versions.reduce((sum, v) => sum + v.count, 0),
            };
        })
        .filter((b) => b.versions.length > 0), [brands]);

    const seriesMap = useMemo(() => {
        const map = {};
        buildDailySeries(statBrands).forEach((s) => { map[s.brand] = s; });
        return map;
    }, [statBrands]);

    // 新增绑定（昨日 / 本周 / 近7日 / 本月 / 近30日）
    // 品牌行 = 区间内该品牌新增绑定数；展开行 = 按版本占比拆分（末个版本补齐误差）
    const growth = useMemo(() => {
        const periods = buildGrowthPeriods();
        const totalCells = periods.reduce((m, p) => { m[p.key] = 0; return m; }, {});
        const rows = statBrands.map((b) => {
            const series = seriesMap[b.brand];
            const cells = {};
            const versionCells = b.versions.map(() => ({}));
            periods.forEach((p) => {
                const brandNew = sumDailyBetween(
                    series,
                    p.start.format('YYYY-MM-DD'),
                    p.end.format('YYYY-MM-DD'),
                );
                cells[p.key] = brandNew;
                totalCells[p.key] += brandNew;
                const lastIdx = b.versions.length - 1;
                let addedAcc = 0;
                b.versions.forEach((v, idx) => {
                    const val = idx === lastIdx
                        ? brandNew - addedAcc
                        : Math.round((brandNew * v.count) / (b.total || 1));
                    addedAcc += val;
                    versionCells[idx][p.key] = val;
                });
            });
            return {
                key: `brand-${b.brand}`,
                name: b.brand,
                isBrand: true,
                cells,
                children: b.versions.map((v, idx) => ({
                    key: `version-${b.brand}-${v.name}`,
                    name: v.name,
                    isVersion: true,
                    cells: versionCells[idx],
                })),
            };
        });
        return { periods, totalCells, rows };
    }, [statBrands, seriesMap]);

    // 趋势数据：每个 X 坐标（周/月）按 App-系统-区域 拆出累计绑定数（不含被屏蔽版本）
    const trend = useMemo(() => {
        const periods = buildTrendPeriods(trendUnit);
        const versionList = [];
        const versionMap = {};
        statBrands.forEach((b) => {
            b.versions.forEach((v, vi) => {
                const item = {
                    name: v.name,
                    brand: b.brand,
                    // 同品牌两个版本用同色深浅区分
                    color: vi === 0 ? b.color : `${b.color}99`,
                    bound: [],
                };
                versionList.push(item);
                versionMap[v.name] = item;
            });
        });

        const totals = [];
        periods.forEach((p) => {
            let periodBound = 0;
            statBrands.forEach((b) => {
                const brandBound = brandTotalAt(b, p.endKey, seriesMap[b.brand]);
                const lastIdx = b.versions.length - 1;
                // 绑定数按版本占比拆分，末位补齐，保证各版本之和 = 品牌累计绑定数
                let boundAcc = 0;
                const boundVals = b.versions.map((v, idx) => {
                    if (idx === lastIdx) return brandBound - boundAcc;
                    const val = Math.round((brandBound * v.count) / (b.total || 1));
                    boundAcc += val;
                    return val;
                });
                b.versions.forEach((v, idx) => { versionMap[v.name].bound.push(boundVals[idx]); });
                periodBound += brandBound;
            });
            totals.push(periodBound);
        });

        return { labels: periods.map((p) => p.label), versionList, totals };
    }, [statBrands, seriesMap, trendUnit]);

    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return;
        // 屏蔽全部 App 时空态会卸载图表容器，恢复后需重新挂载实例
        if (chartInstance.current && chartInstance.current.getDom() !== chartRef.current) {
            chartInstance.current.dispose();
            chartInstance.current = null;
        }
        if (!chartInstance.current) chartInstance.current = echarts.init(chartRef.current);

        // 分组柱：每个 X 坐标多根柱，一根柱 = 一个 App名称-系统-区域的累计绑定数
        const series = trend.versionList.map((v) => ({
            name: v.name,
            type: 'bar',
            data: v.bound,
            itemStyle: { color: v.color },
            barMaxWidth: 16,
            barGap: '10%',
        }));

        chartInstance.current.setOption({
            legend: { type: 'scroll', top: 8, itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11 } },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const idx = params[0].dataIndex;
                    const lines = [
                        `<div style="font-weight:600;margin-bottom:4px">${trend.labels[idx]}｜合计 ${trend.totals[idx].toLocaleString()} 台</div>`,
                    ];
                    trend.versionList.forEach((v) => {
                        lines.push(`<div>${v.name}：<b>${v.bound[idx].toLocaleString()} 台</b></div>`);
                    });
                    return lines.join('');
                },
            },
            grid: { top: 56, left: 70, right: 30, bottom: 40 },
            xAxis: {
                type: 'category',
                data: trend.labels,
                axisLine: { lineStyle: { color: '#999' } },
            },
            yAxis: {
                type: 'value',
                name: '绑定设备数',
                min: 0,
                minInterval: 1,
                nameTextStyle: { fontSize: 11 },
                axisLabel: { formatter: (v) => Math.floor(v) },
            },
            series,
        }, { replaceMerge: ['series'] });
    }, [trend]);

    useEffect(() => {
        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    const handleRoleChange = (role) => {
        setMockRole(role);
        setEntId('all');
    };

    // ─── 屏蔽配置（平台管理端） ───
    // 查询条件：App 名称（模糊）/ AppID（精准）/ App企业名称（模糊）/ App企业ID（精准）
    //           指定企业名称（模糊）/ 指定企业ID（精准）
    const hasBlockQuery = Object.values(blockQuery).some((v) => v !== '');

    const filteredBlockConfigs = useMemo(() => {
        const appName = blockQuery.appName.trim().toLowerCase();
        const appId = blockQuery.appId.trim();
        const appEntName = blockQuery.appEntName.trim().toLowerCase();
        const appEntId = blockQuery.appEntId.trim();
        const entName = blockQuery.entName.trim().toLowerCase();
        const entIdKey = blockQuery.entId.trim();
        return blockConfigs.filter((c) => {
            // 指定企业：名称模糊 + 企业ID精准
            if (entName && !c.entName.toLowerCase().includes(entName)) return false;
            if (entIdKey && c.entId !== entIdKey) return false;
            // 屏蔽 App：App 名称模糊 + AppID 精准
            if (appName && !c.appIds.some((id) => (APP_VERSION_MAP[id]?.version || '').toLowerCase().includes(appName))) return false;
            if (appId && !c.appIds.some((id) => id === appId)) return false;
            // App 企业：按被屏蔽 App 的归属企业匹配（名称模糊 + 企业ID精准）
            const owners = ownersOfApps(c.appIds);
            if (appEntName && !owners.some((o) => o.entName.toLowerCase().includes(appEntName))) return false;
            if (appEntId && !owners.some((o) => o.entId === appEntId)) return false;
            return true;
        });
    }, [blockConfigs, blockQuery]);

    // 查询：把输入中的条件生效；重置：一次清空输入中与已生效的条件
    // 仅点击「查询」按钮触发过滤，输入框不做实时过滤、不响应回车
    const applyBlockQuery = () => setBlockQuery({ ...blockQueryInput });

    const resetBlockQuery = () => {
        setBlockQueryInput({ ...EMPTY_BLOCK_QUERY });
        setBlockQuery({ ...EMPTY_BLOCK_QUERY });
    };

    const openBlockForm = (record) => {
        setEditingBlock(record || null);
        setBlockFormVisible(true);
    };

    useEffect(() => {
        if (!blockFormVisible) return;
        if (editingBlock) {
            blockForm.setFieldsValue({ entId: editingBlock.entId, appIds: editingBlock.appIds });
        } else {
            blockForm.resetFields();
        }
    }, [blockFormVisible, editingBlock, blockForm]);

    const submitBlockForm = async () => {
        let values;
        try {
            values = await blockForm.validateFields();
        } catch (e) {
            return;
        }
        const ent = MOCK_ENTERPRISES.find((e) => e.value === values.entId);
        const now = dayjs().format('YYYY-MM-DD HH:mm');
        // TODO: 联调时替换为接口保存
        let next;
        if (editingBlock) {
            next = blockConfigs.map((c) => (c.id === editingBlock.id
                ? { ...c, entId: values.entId, entName: ent?.label || c.entName, appIds: values.appIds, updatedAt: now }
                : c));
        } else {
            const exist = blockConfigs.find((c) => c.entId === values.entId);
            if (exist) {
                // 同一企业只保留一条配置，新增时按 App 去重合并
                next = blockConfigs.map((c) => (c.id === exist.id
                    ? { ...c, appIds: Array.from(new Set([...c.appIds, ...values.appIds])), updatedAt: now }
                    : c));
                message.info(`「${ent?.label}」已存在屏蔽配置，已合并本次选择的 App`);
            } else {
                next = [
                    ...blockConfigs,
                    {
                        id: `BLK-${dayjs().format('YYYYMMDD-HHmmss')}`,
                        entId: values.entId,
                        entName: ent?.label || values.entId,
                        appIds: values.appIds,
                        operator: '当前登录用户',
                        updatedAt: now,
                    },
                ];
            }
        }
        setBlockConfigs(next);
        writeBlockConfigs(next);
        setBlockFormVisible(false);
        message.success(editingBlock ? '屏蔽配置已更新' : '屏蔽配置已添加');
    };

    const removeBlockConfig = (record) => {
        const next = blockConfigs.filter((c) => c.id !== record.id);
        setBlockConfigs(next);
        writeBlockConfigs(next);
        message.success('屏蔽配置已删除');
    };

    // ─── 导出：选择时间范围后展示预览 ───
    const openExport = () => {
        setExportVisible(true);
        setExportRangeKey(null);
        setExportPreview(null);
    };

    const handleExportRangeChange = (key) => {
        setExportRangeKey(key);
        if (!key) {
            setExportPreview(null);
            return;
        }
        const exportRange = buildExportRange(key);
        const rows = buildExportRows(statBrands, exportRange, seriesMap);
        // 预览里的累计绑定取导出区间「末日」的快照值；区间新增 = 导出区间内每日新增之和
        const endKey = exportRange[1].format('YYYY-MM-DD');
        let endBound = 0;
        statBrands.forEach((b) => {
            endBound += brandTotalAt(b, endKey, seriesMap[b.brand]);
        });
        const rangeAdded = rows.reduce((sum, r) => sum + r.added, 0);
        setExportPreview({
            exportRange,
            dayCount: exportRange[1].startOf('day').diff(exportRange[0].startOf('day'), 'day') + 1,
            versionCount: statBrands.reduce((sum, b) => sum + b.versions.length, 0),
            rowCount: rows.length,
            endBound,
            rangeAdded,
            rows,
        });
    };

    const confirmExport = () => {
        if (!exportPreview) return;
        if (exportPreview.rowCount <= 0) {
            message.warning('当前导出范围为 0 行，请调整导出时间范围或屏蔽配置');
            return;
        }
        setExportLoading(true);
        // TODO: 联调时替换为接口导出（Excel）
        const header = '日期,App名称-系统-区域,累计绑定设备数,当日新增绑定数';
        const lines = exportPreview.rows.map((r) => [r.date, r.version, r.bound, r.added].join(','));
        const csvContent = '\uFEFF' + header + '\n' + lines.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `设备品牌统计_${exportPreview.exportRange[0].format('YYYYMMDD')}-${exportPreview.exportRange[1].format('YYYYMMDD')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        setTimeout(() => {
            setExportLoading(false);
            message.success(`导出成功（共 ${exportPreview.rowCount.toLocaleString()} 行）`);
            setExportVisible(false);
        }, 600);
    };

    // 合计只统计「未屏蔽」部分；卡片区仍展示被屏蔽 App 的原始数据量
    const totalCount = statBrands.reduce((sum, b) => sum + b.total, 0);
    const versionCount = statBrands.reduce((sum, b) => sum + b.versions.length, 0);
    const blockedTotalSum = brands.reduce((sum, b) => sum + b.blockedTotal, 0);

    // 「设备绑定增长」表格数据：合计行（不可展开）+ 各品牌行（可展开到版本）
    const growthDataSource = [
        { key: 'total', name: '合计', isTotal: true, cells: growth.totalCells },
        ...growth.rows,
    ];

    // 空态文案：平台端可指向「屏蔽配置」；企业端为静默过滤，不暴露屏蔽概念
    const emptyTip = isPlatform
        ? '当前无纳入统计的 App，请调整「屏蔽配置」'
        : '暂无可展示的数据';

    // 屏蔽配置列表列定义
    const blockColumns = [
        {
            title: '指定企业',
            dataIndex: 'entName',
            width: 260,
            render: (name, record) => (
                <div>
                    <div style={{ color: '#303133' }}>{name}</div>
                    <div style={{ color: '#999', fontSize: 12 }}>{record.entId}</div>
                </div>
            ),
        },
        {
            title: '屏蔽 App',
            dataIndex: 'appIds',
            width: 110,
            render: (appIds, record) => (
                <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() => setBlockDetail(record)}
                >
                    {appIds.length}
                </Button>
            ),
        },
        {
            title: 'App 企业',
            dataIndex: 'appIds',
            width: 260,
            render: (appIds) => {
                const owners = ownersOfApps(appIds);
                if (owners.length === 0) return <Text type="secondary">-</Text>;
                if (owners.length === 1) {
                    return (
                        <div>
                            <div style={{ color: '#303133' }}>{owners[0].entName}</div>
                            <div style={{ color: '#999', fontSize: 12 }}>{owners[0].entId}</div>
                        </div>
                    );
                }
                return (
                    <Tooltip
                        title={(
                            <div>
                                {owners.map((o) => <div key={o.entId}>{o.entName} {o.entId}</div>)}
                            </div>
                        )}
                    >
                        <span style={{ color: '#1890ff', cursor: 'help' }}>{owners.length} 个企业</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '更新时间',
            dataIndex: 'updatedAt',
            width: 150,
            render: (value, record) => (
                <div>
                    <div style={{ fontSize: 12, color: '#606266' }}>{value}</div>
                    <div style={{ fontSize: 12, color: '#999' }}>{record.operator}</div>
                </div>
            ),
        },
        {
            title: '操作',
            dataIndex: 'id',
            width: 110,
            render: (id, record) => (
                <Space size={4}>
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openBlockForm(record)}>
                        编辑
                    </Button>
                    <Popconfirm
                        title="确认删除该屏蔽配置？"
                        description="删除后该企业对应的 App 绑定数据将恢复统计。"
                        okText="删除"
                        cancelText="取消"
                        onConfirm={() => removeBlockConfig(record)}
                    >
                        <Button type="link" size="small" danger style={{ padding: 0 }}>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            {/* 标题行 + 最后更新时间 + mock 角色视角切换 */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={4} style={{ margin: 0 }}>设备品牌统计</Title>
                <Space size={16}>
                    <Tooltip title={`数据来源数仓，按日推送（T+1），数据截至 ${DATA_AS_OF_DATE}；本页所有数据均非实时`}>
                        <Space size={6} style={{ cursor: 'help' }}>
                            <ClockCircleOutlined style={{ color: '#faad14' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>最后更新时间：{LAST_UPDATE_TIME}</Text>
                        </Space>
                    </Tooltip>
                    <span style={{ width: 1, height: 16, background: '#d9d9d9' }} />
                    <Space size={8}>
                        <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
                        <Segmented value={mockRole} onChange={handleRoleChange} options={ROLE_OPTIONS} />
                    </Space>
                </Space>
            </div>

            {/* 筛选区 */}
            <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <Space size={24} wrap>
                        {isPlatform && (
                            <Space size={8}>
                                <span style={{ color: '#606266' }}>所属企业</span>
                                <Select
                                    value={entId}
                                    onChange={setEntId}
                                    style={{ width: 240 }}
                                    showSearch
                                    optionFilterProp="label"
                                    options={MOCK_ENTERPRISES}
                                />
                                {blockedVersions.length > 0 && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        该企业已屏蔽 {blockedVersions.length} 个 App-系统-区域的绑定数据
                                    </Text>
                                )}
                            </Space>
                        )}
                    </Space>
                    <Space size={8}>
                        <Button icon={<DownloadOutlined />} onClick={openExport} disabled={statBrands.length === 0}>导出</Button>
                        {isPlatform && (
                            <Button icon={<StopOutlined />} onClick={() => setBlockVisible(true)}>屏蔽配置</Button>
                        )}
                    </Space>
                </div>
            </Card>

            {/* 品牌设备规模（累计绑定） */}
            <Card
                title="品牌设备规模"
                extra={(
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        数据更新：{LAST_UPDATE_TIME}
                    </Text>
                )}
                bordered={false}
                style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            >
                {brands.length === 0 ? (
                    <Empty description={emptyTip} />
                ) : (
                <Row gutter={[16, 16]}>
                    <Col flex="1 1 0">
                        <Card bordered={false} style={{ borderRadius: 12, background: '#fafafa', height: '100%' }}>
                            <Statistic
                                title="合计（累计绑定）"
                                value={totalCount}
                                suffix="台"
                                valueStyle={{ fontSize: 28, fontWeight: 600, color: '#1890ff' }}
                            />
                            <div style={{ marginTop: 10, borderTop: '1px dashed #f0f0f0', paddingTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    共 {statBrands.length} 个品牌 / {versionCount} 个版本
                                </Text>
                                {blockedVersions.length > 0 && (
                                    <div style={{ marginTop: 4 }}>
                                        <Text style={{ fontSize: 12, color: '#fa8c16' }}>
                                            已剔除 {blockedVersions.length} 个被屏蔽的 App-系统-区域（{blockedTotalSum.toLocaleString()} 台）
                                        </Text>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>
                    {brands.map((b) => (
                        <Col flex="1 1 0" key={b.brand}>
                            <Card bordered={false} style={{ borderRadius: 12, height: '100%', position: 'relative' }}>
                                {b.allBlocked && (
                                    <Tag
                                        color="error"
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            margin: 0,
                                            borderRadius: '0 12px 0 8px',
                                            fontSize: 12,
                                        }}
                                    >
                                        已屏蔽
                                    </Tag>
                                )}
                                <Statistic
                                    title={`${b.brand}（累计绑定）`}
                                    value={b.total}
                                    suffix="台"
                                    valueStyle={{ fontSize: 28, fontWeight: 600, color: b.allBlocked ? '#bfbfbf' : b.color }}
                                />
                                <div style={{ marginTop: 10, borderTop: '1px dashed #f0f0f0', paddingTop: 8 }}>
                                    <div style={{ display: 'flex', fontSize: 12, color: '#999', lineHeight: '20px' }}>
                                        <span style={{ flex: 1 }}>版本</span>
                                        <span style={{ width: 80, textAlign: 'right' }}>累计绑定</span>
                                    </div>
                                    {b.versions.map((v) => (
                                        <div key={v.name} style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: '#606266', lineHeight: '20px' }}>
                                            <span
                                                title={v.name}
                                                style={{
                                                    flex: 1,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    color: v.blocked ? '#bfbfbf' : '#606266',
                                                }}
                                            >
                                                {v.name}
                                            </span>
                                            {v.blocked && (
                                                <Tag
                                                    color="error"
                                                    style={{ flex: 'none', margin: '0 4px 0 0', fontSize: 10, lineHeight: '16px', height: 16, padding: '0 4px' }}
                                                >
                                                    已屏蔽
                                                </Tag>
                                            )}
                                            <span style={{ width: 80, textAlign: 'right', color: v.blocked ? '#bfbfbf' : '#606266' }}>
                                                {v.count.toLocaleString()}
                                            </span>
                                        </div>
                                    ))}
                                    {b.blockedVersionCount > 0 && (
                                        <div style={{ marginTop: 6 }}>
                                            <Text style={{ fontSize: 12, color: '#fa8c16' }}>
                                                {b.allBlocked
                                                    ? '该 App 已全部屏蔽，不计入合计'
                                                    : `已屏蔽 ${b.blockedVersionCount} 个版本，不计入合计`}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
                )}
            </Card>

            {/* 设备绑定增长（昨日 / 本周 / 近7日 / 本月 / 近30日 新增绑定设备数） */}
            <Card
                title="设备绑定增长"
                extra={(
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        数据更新：{LAST_UPDATE_TIME}
                    </Text>
                )}
                bordered={false}
                style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            >
                {statBrands.length === 0 ? (
                    <Empty description={emptyTip} />
                ) : (
                    <>
                        <Table
                            rowKey="key"
                            size="small"
                            pagination={false}
                            dataSource={growthDataSource}
                            scroll={{ x: 820 }}
                            onRow={(record) => ({
                                style: record.isTotal ? { background: '#fafafa', fontWeight: 600 } : {},
                            })}
                            columns={[
                                {
                                    title: '品牌 / 版本',
                                    dataIndex: 'name',
                                    width: 280,
                                    render: (name, record) => (
                                        record.isBrand
                                            ? <Text strong>{name}</Text>
                                            : record.isTotal
                                                ? <Text strong>{name}</Text>
                                                : <Text type="secondary" style={{ paddingLeft: 8 }}>{name}</Text>
                                    ),
                                },
                                ...growth.periods.map((p) => ({
                                    title: (
                                        <Tooltip
                                            title={`${p.start.format('M月D日')} ~ ${p.end.format('M月D日')} 区间内新增绑定设备数${p.key === 'thisWeek' ? '（本周从周一算起）' : ''}`}
                                        >
                                            <span style={{ borderBottom: '1px dashed #d9d9d9', cursor: 'help' }}>{p.label}</span>
                                        </Tooltip>
                                    ),
                                    key: p.key,
                                    align: 'right',
                                    width: 108,
                                    render: (_, record) => (
                                        <Text strong={record.isTotal}>
                                            {Number(record.cells[p.key] || 0).toLocaleString()}
                                        </Text>
                                    ),
                                })),
                            ]}
                        />
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                口径：数仓按日推送（T+1），五个窗口均以数据截止日 {DATA_AS_OF_DATE} 为终点；本周从周一算起；近7日 / 本月 / 近30日等窗口之间允许重叠。品牌行可展开查看 App名称-系统-区域 版本明细。
                            </Text>
                        </div>
                    </>
                )}
            </Card>

            {/* App 绑定情况（按周/按月，每个 App-系统-区域 一根柱） */}
            <Card
                title="App 绑定情况"
                extra={(
                    <Segmented value={trendUnit} onChange={setTrendUnit} options={TREND_UNIT_OPTIONS} />
                )}
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}
            >
                {statBrands.length === 0 ? (
                    <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty description={emptyTip} />
                    </div>
                ) : (
                    <div ref={chartRef} style={{ width: '100%', height: 400 }} />
                )}
            </Card>

            {/* ══════════════════════════════════════════════
                 导出 Modal：按天导出 日期 × App版本
                 ══════════════════════════════════════════════ */}
            <Modal
                title="导出设备品牌统计"
                open={exportVisible}
                onCancel={() => setExportVisible(false)}
                width={560}
                footer={
                    exportPreview
                        ? [
                            <Button key="cancel" onClick={() => setExportVisible(false)}>取消</Button>,
                            <Button key="export" type="primary" icon={<DownloadOutlined />} loading={exportLoading} onClick={confirmExport}>
                                导出 Excel
                            </Button>,
                        ]
                        : [
                            <Button key="close" onClick={() => setExportVisible(false)}>关闭</Button>,
                        ]
                }
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {/* ① 导出时间范围 */}
                    <div>
                        <Text strong>① 导出时间范围</Text>
                        <Select
                            style={{ width: '100%', marginTop: 8 }}
                            placeholder="请选择导出时间范围"
                            allowClear
                            value={exportRangeKey}
                            onChange={handleExportRangeChange}
                            options={EXPORT_RANGE_OPTIONS}
                        />
                        <div style={{ marginTop: 6 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                按天导出：每一天 × 每个 App版本（App名称-系统-区域）各一行
                            </Text>
                        </div>
                    </div>

                    {/* ② 数据预览 */}
                    {exportPreview && (
                        <Card size="small" style={{ background: '#fafafa', borderRadius: 8 }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                                <div>
                                    <Text type="secondary">导出范围：</Text>
                                    <Text strong>
                                        {exportPreview.exportRange[0].format('YYYY-MM-DD')} ~ {exportPreview.exportRange[1].format('YYYY-MM-DD')}
                                    </Text>
                                </div>
                                <div><Text type="secondary">天数：</Text><Text strong>{exportPreview.dayCount} 天</Text></div>
                                <div><Text type="secondary">覆盖版本：</Text><Text strong>{exportPreview.versionCount} 个</Text></div>
                                <div>
                                    <Text type="secondary">期末累计绑定设备数：</Text>
                                    <Text strong style={{ color: '#1890ff' }}>{exportPreview.endBound.toLocaleString()} 台</Text>
                                </div>
                                <div>
                                    <Text type="secondary">区间新增绑定设备数：</Text>
                                    <Text strong style={{ color: '#52c41a' }}>{exportPreview.rangeAdded.toLocaleString()} 台</Text>
                                </div>
                                <div>
                                    <Text type="secondary">范围：</Text>
                                    <Text strong>
                                        {isPlatform
                                            ? (MOCK_ENTERPRISES.find((e) => e.value === entId)?.label || '全部企业')
                                            : (mockRole === 'app' ? '本企业名下 App' : '本企业')}
                                    </Text>
                                </div>
                                {blockedVersions.length > 0 && (
                                    <div>
                                        <Text type="secondary">已屏蔽 App 版本：</Text>
                                        <Text strong style={{ color: '#faad14' }}>{blockedVersions.length} 个</Text>
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: 8, padding: '4px 8px', background: '#e6f4ff', borderRadius: 4 }}>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    导出内容：{exportPreview.dayCount} 天 × {exportPreview.versionCount} 个版本 = 共 {exportPreview.rowCount.toLocaleString()} 行；
                                    表头：日期、App名称-系统-区域、累计绑定设备数、当日新增绑定数（累计绑定为「截至当天」的累计值）
                                </Text>
                            </div>
                        </Card>
                    )}
                </Space>
            </Modal>

            {/* ══════════════════════════════════════════════
                 屏蔽配置 Drawer（平台管理端）
                 指定企业 → 屏蔽指定 App-系统-区域 的设备绑定数据
                 ══════════════════════════════════════════════ */}
            <Drawer
                title="屏蔽配置"
                open={blockVisible}
                onClose={() => setBlockVisible(false)}
                width={920}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        type="info"
                        showIcon
                        message="此功能可指定某企业，屏蔽指定 App 的设备绑定数据"
                        description="常见场景：平台测试人员在企业未知的 App 上测试产生脏数据，导致企业质疑。屏蔽后，该企业在《设备品牌统计》中对应 App-系统-区域的绑定数据不再计入统计（不影响其他企业），配置长期保存。"
                    />

                    {/* 查询条件：App / App企业 / 指定企业 */}
                    <Card size="small" bordered={false} style={{ background: '#fafafa', borderRadius: 8 }}>
                        <Space size={12} wrap>
                            <Input
                                allowClear
                                style={{ width: 170 }}
                                placeholder="App 名称（模糊）"
                                value={blockQueryInput.appName}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, appName: e.target.value }))}
                            />
                            <Input
                                allowClear
                                style={{ width: 150 }}
                                placeholder="AppID（精准）"
                                value={blockQueryInput.appId}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, appId: e.target.value.trim() }))}
                            />
                            <Input
                                allowClear
                                style={{ width: 170 }}
                                placeholder="App企业名称（模糊）"
                                value={blockQueryInput.appEntName}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, appEntName: e.target.value }))}
                            />
                            <Input
                                allowClear
                                style={{ width: 160 }}
                                placeholder="App企业ID（精准）"
                                value={blockQueryInput.appEntId}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, appEntId: e.target.value.trim() }))}
                            />
                            <Input
                                allowClear
                                style={{ width: 170 }}
                                placeholder="指定企业名称（模糊）"
                                value={blockQueryInput.entName}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, entName: e.target.value }))}
                            />
                            <Input
                                allowClear
                                style={{ width: 160 }}
                                placeholder="指定企业ID（精准）"
                                value={blockQueryInput.entId}
                                onChange={(e) => setBlockQueryInput((prev) => ({ ...prev, entId: e.target.value.trim() }))}
                            />
                            <Button type="primary" onClick={applyBlockQuery}>查询</Button>
                            <Button onClick={resetBlockQuery}>重置</Button>
                        </Space>
                    </Card>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            共 {filteredBlockConfigs.length} 条配置{hasBlockQuery ? `（已过滤，全部 ${blockConfigs.length} 条）` : ''}
                        </Text>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openBlockForm()}>添加</Button>
                    </div>

                    <Table
                        rowKey="id"
                        size="small"
                        dataSource={filteredBlockConfigs}
                        columns={blockColumns}
                        scroll={{ x: 890 }}
                        pagination={{
                            pageSize: 10,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条`,
                        }}
                    />

                </Space>
            </Drawer>

            {/* ══════════════════════════════════════════════
                 添加 / 编辑屏蔽配置
                 ══════════════════════════════════════════════ */}
            <Modal
                title={editingBlock ? '编辑屏蔽配置' : '添加屏蔽配置'}
                open={blockFormVisible}
                onCancel={() => setBlockFormVisible(false)}
                onOk={submitBlockForm}
                okText="保存"
                cancelText="取消"
                width={640}
            >
                <Form form={blockForm} layout="vertical" style={{ marginTop: 8 }}>
                    <Form.Item
                        name="entId"
                        label="指定企业"
                        rules={[{ required: true, message: '请选择指定企业' }]}
                    >
                        <Select
                            showSearch
                            placeholder="请选择要屏蔽数据的企业"
                            optionFilterProp="label"
                            options={MOCK_ENTERPRISES
                                .filter((e) => e.value !== 'all')
                                .map((e) => ({ value: e.value, label: `${e.label}（${e.value}）` }))}
                        />
                    </Form.Item>
                    <Form.Item
                        name="appIds"
                        label="屏蔽 App"
                        extra="屏蔽粒度为 App-系统-区域；同一 App 的不同系统-区域需分别选择"
                        rules={[{ required: true, message: '请选择要屏蔽的 App' }]}
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            placeholder="按 App 名称 / AppID 搜索"
                            optionFilterProp="label"
                            options={MOCK_APP_VERSIONS.map((a) => ({
                                value: a.appId,
                                label: `${a.version}（${a.appId}）· ${a.entName}`,
                            }))}
                        />
                    </Form.Item>
                    <Alert
                        type="warning"
                        showIcon
                        message="屏蔽后，该企业在《设备品牌统计》中对应 App-系统-区域的绑定数据不再计入统计（不影响其他企业）。"
                    />
                </Form>
            </Modal>

            {/* ══════════════════════════════════════════════
                 屏蔽 App 明细：树形（一级 App企业 / 二级 App-系统-区域）
                 ══════════════════════════════════════════════ */}
            <Modal
                title="屏蔽 App 明细"
                open={!!blockDetail}
                onCancel={() => setBlockDetail(null)}
                width={600}
                footer={[<Button key="close" onClick={() => setBlockDetail(null)}>关闭</Button>]}
            >
                {blockDetail && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                            <div>
                                <Text type="secondary">指定企业：</Text>
                                <Text strong>{blockDetail.entName}（{blockDetail.entId}）</Text>
                            </div>
                            <div>
                                <Text type="secondary">屏蔽 App：</Text>
                                <Text strong>{blockDetail.appIds.length} 个</Text>
                            </div>
                        </div>
                        <div style={{ maxHeight: 400, overflow: 'auto', padding: '4px 0' }}>
                            <Tree
                                treeData={buildBlockAppTree(blockDetail.appIds)}
                                defaultExpandAll
                                selectable={false}
                                showLine
                            />
                        </div>
                    </Space>
                )}
            </Modal>
        </div>
    );
};

export default DeviceBrand;
